<template>
  <i-form
    v-scroll-to-invalid
    class="form"
    name="film-ribbons-form"
    @submit="handleSubmit"
  >
    <div class="dimensions">
      <strapi-label
        v-if="labels[FilmRibbonsLabels.dimensions]"
        :label="labels[FilmRibbonsLabels.dimensions]"
      />

      <div class="form-field-group">
        <!--width field-->
        <zaproo-input
          v-if="fields[FilmRibbonsFields.width]"
          v-model="form.width"
          type="number"
          name="width"
          :rules="`${fieldRules[FilmRibbonsFields.width]}|minValue:80`"
          :placeholder="fields[FilmRibbonsFields.width].placeholder"
          :description="fields[FilmRibbonsFields.width].description"
          class="form-field"
        >
          <template #label>
            <special-order-form-input-label
              :field="fields[FilmRibbonsFields.width]"
            />
          </template>
        </zaproo-input>
      </div>
    </div>

    <div class="material mt-6">
      <strapi-label
        v-if="labels[FilmRibbonsLabels.material]"
        :label="labels[FilmRibbonsLabels.material]"
      />
      <div class="form-field-group">
        <!--thickness field-->
        <zaproo-select
          v-if="fields[FilmRibbonsFields.thickness]"
          v-model="form.thickness"
          :removable="false"
          :rules="fieldRules[FilmRibbonsFields.thickness]"
          :placeholder="$t('Select option')"
          name="thickness"
          dropdown-classes="py-2 mt-2"
          class="form-field checkmark"
        >
          <template #label>
            <special-order-form-input-label
              :field="fields[FilmRibbonsFields.thickness]"
            />
          </template>

          <zaproo-select-option
            v-for="option in fields[FilmRibbonsFields.thickness].options"
            :key="option.value"
            :value="option.value"
            >{{ option.title }}</zaproo-select-option
          >
        </zaproo-select>

        <!--otherThickness field-->
        <zaproo-input
          v-if="isOtherThickness && fields[FilmRibbonsFields.otherThickness]"
          v-model="form.otherThickness"
          type="number"
          :rules="fieldRules[FilmRibbonsFields.otherThickness]"
          name="otherThickness"
          :placeholder="fields[FilmRibbonsFields.otherThickness].placeholder"
          :description="fields[FilmRibbonsFields.otherThickness].description"
          class="form-field"
        >
          <template #label>
            <special-order-form-input-label
              :field="fields[FilmRibbonsFields.otherThickness]"
            />
          </template>
        </zaproo-input>
      </div>

      <div class="form-field-group">
        <!--mainColor field-->
        <zaproo-select
          v-if="fields[FilmRibbonsFields.mainColor]"
          v-model="form.mainColor"
          :removable="false"
          :rules="fieldRules[FilmRibbonsFields.mainColor]"
          :placeholder="$t('Select option')"
          name="mainColor"
          dropdown-classes="py-2 mt-2"
          class="form-field checkmark"
        >
          <template #label>
            <special-order-form-input-label
              :field="fields[FilmRibbonsFields.mainColor]"
            />
          </template>

          <zaproo-select-option
            v-for="option in fields[FilmRibbonsFields.mainColor].options"
            :key="option.value"
            :value="option.value"
            >{{ option.title }}</zaproo-select-option
          >
        </zaproo-select>

        <!--otherMainColor field-->
        <zaproo-input
          v-if="isMainColorOther && fields[FilmRibbonsFields.otherMainColor]"
          v-model="form.otherMainColor"
          name="otherMainColor"
          :rules="fieldRules[FilmRibbonsFields.otherMainColor]"
          :placeholder="fields[FilmRibbonsFields.otherMainColor].placeholder"
          :description="fields[FilmRibbonsFields.otherMainColor].description"
          class="form-field"
        >
          <template #label>
            <special-order-form-input-label
              :field="fields[FilmRibbonsFields.otherMainColor]"
            />
          </template>
        </zaproo-input>
      </div>
    </div>

    <div class="print mt-6">
      <strapi-label :label="labels[FilmRibbonsLabels.print]" />
      <div class="form-field-group">
        <div>
          <!--numberOfPrintColors field-->
          <zaproo-select
            v-if="fields[FilmRibbonsFields.numberOfPrintColors]"
            v-model="form.numberOfPrintColors"
            :removable="false"
            :rules="fieldRules[FilmRibbonsFields.numberOfPrintColors]"
            :placeholder="$t('Select option')"
            name="numberOfPrintColors"
            dropdown-classes="py-2 mt-2"
            class="form-field checkmark"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[FilmRibbonsFields.numberOfPrintColors]"
              />
            </template>

            <zaproo-select-option
              v-for="option in fields[FilmRibbonsFields.numberOfPrintColors]
                .options"
              :key="option.value"
              :value="option.value"
              >{{ option.title }}</zaproo-select-option
            >
          </zaproo-select>

          <!--designFile field-->
          <zaproo-file-input
            v-if="isPrintDesignFile && fields[FilmRibbonsFields.designFile]"
            v-model="form.designFile"
            class="mt-3"
            name="designFiles"
            :rules="fieldRules[FilmRibbonsFields.designFile]"
            multiple
            :total-size-of-files="INPUT_FILE_TOTAL_SIZE"
            :placeholder="fields[FilmRibbonsFields.designFile].placeholder"
            file-item-classes="h-10"
            remove-button-classes="!p-0"
          >
            <template #label>
              <special-order-form-input-label
                :field="fields[FilmRibbonsFields.designFile]"
              />
            </template>
            <template #removeFileText>
              <icons-icon-trash />
              {{ $t("Remove") }}
            </template>
            <template #fileError="{ errors, clear }">
              <div class="mb-2 grid grid-cols-1 gap-2">
                <file-input-error-notification
                  :clear="clear"
                  :errors="errors"
                />
              </div>
            </template>
          </zaproo-file-input>
        </div>
      </div>
    </div>

    <special-order-form-general-contacts-part
      v-model="form"
      class="general-contacts-form"
      :loading="loading"
    >
      <div class="form-field">
        <!--amountKg field-->
        <zaproo-input
          v-if="fields[FilmRibbonsFields.amountKg]"
          v-model="form.amountKg"
          type="number"
          name="amountKg"
          :rules="fieldRules[FilmRibbonsFields.amountKg]"
          :placeholder="fields[FilmRibbonsFields.amountKg].placeholder"
          :description="
            t('Minimum quantity is 100kg. Total: {n} meters', {
              n: totalAmount,
            })
          "
        >
          <template #label>
            <special-order-form-input-label
              :field="fields[FilmRibbonsFields.amountKg]"
            />
          </template>
        </zaproo-input>
      </div>
    </special-order-form-general-contacts-part>
  </i-form>
</template>

<script setup lang="ts">
import { Form as IForm, FormActions } from "vee-validate";
import SpecialOrderFormInputLabel from "../SpecialOrderFormInputLabel.vue";
import { FilmRibbonsFields, FilmRibbonsLabels } from "./types";
import type {
  StrapiField,
  StrapiLabel as StrapiLabelType,
  StrapiSpecialOrders,
} from "~/types/strapi";

import StrapiLabel from "~/components/strapi/components/shared/StrapiLabel.vue";
import { getFieldDefaultModel } from "~/modules/special-orders/helpers";
import { INPUT_FILE_TOTAL_SIZE } from "~/modules/special-orders/variables";
import FileInputErrorNotification from "~/modules/special-orders/components/SpecialOrderForm/FileInputErrorNotification.vue";
import { useFormFieldCleaner } from "~/modules/special-orders/composables/useFormFieldCleaner";

const props = defineProps<{
  specialOrder: StrapiSpecialOrders;
}>();

const { collection: labelCollection } =
  useSpecialOrderUidDictionary<StrapiLabelType>(props.specialOrder.labels);

const { collection: fieldsCollection } =
  useSpecialOrderUidDictionary<StrapiField>(props.specialOrder.fields);

const { sendRequest, loading } = useSpecialOrdersSubmit(props.specialOrder);

// For prevent type error https://github.com/jojomatik/nuxt-3-base/issues/5
const { t } = useI18n();

const ROLL_BASE_LENGTH = 1096972;

const labels = computed(() => labelCollection(FilmRibbonsLabels));
const fields = computed(() => fieldsCollection(FilmRibbonsFields));
const fieldRules = useSpecialOrderFieldRules<typeof FilmRibbonsFields>(
  fields.value,
);

const form = ref<ReturnType<typeof resetForm>>(resetForm());

const isPrintDesignFile = computed<boolean>(
  () => form.value.numberOfPrintColors !== "0",
);

const isOtherThickness = computed<boolean>(
  () => form.value.thickness === "other",
);

const isMainColorOther = computed<boolean>(
  () => form.value.mainColor === "other",
);

const totalAmount = computed<number>(() => {
  const currentThickness = !isOtherThickness.value
    ? +form.value.thickness
    : +form.value.otherThickness;
  const finalValue =
    ((ROLL_BASE_LENGTH / +form.value.width) * +form.value.amountKg) /
    currentThickness;

  return isNaN(finalValue) ? 0 : finalValue;
});

function resetForm() {
  return {
    width: getFieldDefaultModel(fields.value[FilmRibbonsFields.width]) || "",
    thickness:
      getFieldDefaultModel(fields.value[FilmRibbonsFields.thickness]) || "",
    otherThickness:
      getFieldDefaultModel(fields.value[FilmRibbonsFields.otherThickness]) ||
      "",
    mainColor: getFieldDefaultModel(fields.value[FilmRibbonsFields.mainColor]),
    otherMainColor: getFieldDefaultModel(
      fields.value[FilmRibbonsFields.otherMainColor],
    ),
    numberOfPrintColors: getFieldDefaultModel(
      fields.value[FilmRibbonsFields.numberOfPrintColors],
    ),
    amountKg:
      getFieldDefaultModel(fields.value[FilmRibbonsFields.amountKg]) || "",
    designFile: [],
    email: "",
    phone: "",
    additionalInfo: "",
  };
}

async function handleSubmit(
  _: Record<string, unknown>,
  { resetForm: reset }: FormActions<Record<string, unknown>>,
) {
  await sendRequest(form.value);
  reset();
  form.value = resetForm();
}

useFormFieldCleaner(
  {
    designFile: {
      default: () => [],
      ref: isPrintDesignFile,
    },
    otherThickness: {
      default: () =>
        getFieldDefaultModel(fields.value[FilmRibbonsFields.otherThickness]) ||
        "",
      ref: isOtherThickness,
    },
    otherMainColor: {
      default: () =>
        getFieldDefaultModel(fields.value[FilmRibbonsFields.otherMainColor]),
      ref: isMainColorOther,
    },
  },
  form,
);
</script>
